import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronRight,
  Clock3,
  Footprints,
  Gauge,
  Medal,
  PenLine,
  Sparkles,
  Trophy,
} from 'lucide-react'
import { api } from './lib/api.js'
import { BottomNavigation, Header } from './components/Navigation.jsx'
import { Modal } from './components/Modal.jsx'
import { GamePage } from './components/Game.jsx'
import { ArchivesPage } from './components/Archives.jsx'
import { PlayerProgressPage } from './components/PlayerProgress.jsx'
import { PrivacyPage } from './components/StaticPages.jsx'
import { recordDailyCompletion } from './lib/progressStore.js'
import { track } from './lib/telemetry.js'

const NICKNAME_KEY = 'sukoji:nickname'
const PENDING_NICKNAME_KEY = 'sukoji:nickname-pending'
const TRAINING_CACHE_PREFIX = 'sukoji:training:'
const INTERNAL_HISTORY_KEY = 'sukojiInternalEntry'
const ROUTES = new Set([
  '/',
  '/entrainement',
  '/classement',
  '/regles',
  '/profil',
  '/archives',
  '/confidentialite',
  '/jouer/quotidien',
  '/jouer/entrainement',
  '/jouer/archive',
])

function plainHistoryState() {
  const state = window.history.state
  if (!state || typeof state !== 'object' || Array.isArray(state)) return {}
  const { sukojiModal: _modal, ...rest } = state
  return rest
}

function readNickname() {
  try {
    return localStorage.getItem(NICKNAME_KEY) || ''
  } catch {
    return ''
  }
}

function readPendingNickname() {
  try {
    return localStorage.getItem(PENDING_NICKNAME_KEY) || ''
  } catch {
    return ''
  }
}

let nicknameSyncPromise = null

function syncPendingNickname() {
  if (nicknameSyncPromise) return nicknameSyncPromise
  nicknameSyncPromise = (async () => {
    while (true) {
      const pending = readPendingNickname()
      if (!pending) return
      try {
        await api.saveProfile(pending)
      } catch {
        return
      }
      try {
        if (localStorage.getItem(PENDING_NICKNAME_KEY) === pending) {
          localStorage.removeItem(PENDING_NICKNAME_KEY)
          return
        }
      } catch {
        return
      }
    }
  })().finally(() => { nicknameSyncPromise = null })
  return nicknameSyncPromise
}

function useRouter() {
  const current = () => {
    const pathname = window.location.pathname === '/'
      ? '/'
      : window.location.pathname.replace(/\/+$/, '') || '/'
    return `${pathname}${window.location.search}`
  }
  const [route, setRoute] = useState(current)
  useEffect(() => {
    if (`${window.location.pathname}${window.location.search}` !== current()) {
      window.history.replaceState(plainHistoryState(), '', current())
    }
    const onPopState = () => setRoute(current())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])
  const navigate = useCallback((path, { replace = false } = {}) => {
    if (path === current()) return
    const state = plainHistoryState()
    if (replace) window.history.replaceState(state, '', path)
    else {
      window.history.pushState(
        { ...state, [INTERNAL_HISTORY_KEY]: true, sukojiFrom: current() },
        '',
        path,
      )
    }
    setRoute(current())
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])
  return { route, pathname: route.split('?')[0], navigate }
}

function isCalendarDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false
  const date = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

function dayLabel(day) {
  const value = isCalendarDay(day) ? new Date(`${day}T12:00:00.000Z`) : new Date()
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Paris',
  }).format(value)
}

function todayLabel() {
  return dayLabel(null)
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '—'
  const minutes = Math.floor(seconds / 60)
  const rest = Math.floor(seconds % 60)
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

function DecorativeGrid() {
  const cells = [2, 0, 1, 3, 4, 1, 3, 2, 1, 4, 2, 0, 3, 2, 4, 1]
  return (
    <div className="hero-grid-wrap" aria-hidden="true">
      <div className="hero-grid-shadow" />
      <div className="hero-grid">
        {cells.map((value, index) => (
          <span key={index} className={value === 0 ? 'is-block' : index % 4 === 1 ? 'is-accent' : ''}>
            {value || ''}
          </span>
        ))}
      </div>
      <span className="hero-sticker"><Sparkles size={16} /> Même grille pour tous</span>
    </div>
  )
}

function HomePage({ navigate, nickname }) {
  const [leaders, setLeaders] = useState([])
  useEffect(() => {
    let active = true
    api.leaderboard(3)
      .then((data) => active && setLeaders(data.entries || data.leaderboard || []))
      .catch(() => {})
    return () => { active = false }
  }, [nickname])

  return (
    <main>
      <section className="hero section-shell">
        <div className="hero-copy">
          <p className="eyebrow"><span className="live-dot" /> Défi du {todayLabel()}</p>
          <h1>Quelques cases.<br /><em>Une seule logique.</em></h1>
          <p className="hero-intro">
            Place chaque nombre au bon endroit, termine la grille du jour et mesure-toi aux autres joueurs.
          </p>
          <div className="hero-actions">
            <button className="button button--primary button--large" type="button" onClick={() => navigate('/jouer/quotidien')}>
              Jouer la grille du jour <ArrowRight size={19} />
            </button>
            <button className="button button--ghost" type="button" onClick={() => navigate('/regles')}>
              <BookOpen size={18} /> Comprendre les règles
            </button>
          </div>
          <div className="hero-meta" aria-label="Informations sur le défi">
            <span><Clock3 size={17} /> Chrono équitable</span>
            <span><Trophy size={17} /> Classement du jour</span>
            <span><PenLine size={17} /> Notes et annulation</span>
          </div>
        </div>
        <DecorativeGrid />
      </section>

      <section className="quick-grid section-shell" aria-label="Choisir une activité">
        <article className="feature-card feature-card--training">
          <div className="feature-icon"><Gauge size={25} /></div>
          <div>
            <p className="eyebrow">À votre rythme</p>
            <h2>S’entraîner sans pression</h2>
            <p>Facile, moyen ou difficile : autant de grilles que vous voulez, sans classement.</p>
          </div>
          <button type="button" className="text-link" onClick={() => navigate('/entrainement')}>
            Choisir un niveau <ChevronRight size={17} />
          </button>
        </article>

        <article className="feature-card feature-card--leaderboard">
          <div className="leaderboard-card-head">
            <div>
              <p className="eyebrow">En direct</p>
              <h2>Les plus rapides aujourd’hui</h2>
            </div>
            <Medal size={26} />
          </div>
          {leaders.length ? (
            <ol className="mini-leaderboard">
              {leaders.map((entry, index) => (
                <li key={entry.id || `${entry.nickname}-${index}`}>
                  <span className={`rank rank--${index + 1}`}>{index + 1}</span>
                  <strong>{entry.nickname || entry.pseudo}</strong>
                  <time>{formatTime(entry.elapsedSeconds ?? entry.elapsed_seconds ?? entry.time)}</time>
                </li>
              ))}
            </ol>
          ) : (
            <div className="empty-leaderboard">
              <Footprints size={24} />
              <p>La journée commence. {nickname}, la première place est libre.</p>
            </div>
          )}
          <button type="button" className="text-link text-link--light" onClick={() => navigate('/classement')}>
            Voir tout le classement <ChevronRight size={17} />
          </button>
        </article>
      </section>

      <section className="how-it-works section-shell">
        <div className="section-heading">
          <p className="eyebrow">Le principe</p>
          <h2>Une règle simple, des déductions délicieuses.</h2>
        </div>
        <div className="steps">
          <article><span>01</span><h3>Repérez un segment</h3><p>Une case noire ou le bord de la grille marque sa fin.</p></article>
          <article><span>02</span><h3>Comptez ses cases</h3><p>Un segment de 4 cases accueille exactement 1, 2, 3 et 4.</p></article>
          <article><span>03</span><h3>Croisez les indices</h3><p>Les lignes et colonnes se répondent jusqu’à l’unique solution.</p></article>
        </div>
      </section>
    </main>
  )
}

const difficulties = [
  {
    id: 'easy',
    label: 'Facile',
    eyebrow: 'Pour démarrer',
    description: 'Une grande grille, mais des indices plus nombreux pour progresser sans se bloquer.',
    meta: '7 × 7 · ≈ 6–12 min',
    color: 'mint',
  },
  {
    id: 'medium',
    label: 'Moyen',
    eyebrow: 'Le bon équilibre',
    description: 'Moins d’indices : il faudra vraiment croiser les candidats entre lignes et colonnes.',
    meta: '8 × 8 · ≈ 12–25 min',
    color: 'sun',
  },
  {
    id: 'hard',
    label: 'Difficile',
    eyebrow: 'Pour les aguerris',
    description: 'Une grille ample et peu guidée, avec des déductions à enchaîner sur plusieurs coups.',
    meta: '9 × 9 · ≈ 20–40 min',
    color: 'coral',
  },
]

function TrainingPage({ navigate }) {
  const startTraining = (difficulty) => {
    try { localStorage.removeItem(`${TRAINING_CACHE_PREFIX}${difficulty}`) } catch { /* stockage indisponible */ }
    navigate(`/jouer/entrainement?niveau=${difficulty}`)
  }

  return (
    <main className="page section-shell">
      <div className="page-heading">
        <p className="eyebrow">Entraînement libre</p>
        <h1>Choisissez votre terrain de jeu.</h1>
        <p>Ici, pas de classement. Chaque lancement crée une nouvelle grille : prenez des notes et progressez à votre rythme.</p>
      </div>
      <div className="difficulty-grid">
        {difficulties.map((item, index) => (
          <article className={`difficulty-card difficulty-card--${item.color}`} key={item.id}>
            <div className="difficulty-topline">
              <span className="difficulty-number">0{index + 1}</span>
              <span className="difficulty-time"><Clock3 size={15} /> {item.meta}</span>
            </div>
            <p className="eyebrow">{item.eyebrow}</p>
            <h2>{item.label}</h2>
            <p>{item.description}</p>
            <button
              className="button button--ink"
              type="button"
              onClick={() => startTraining(item.id)}
            >
              Lancer une grille <ArrowRight size={18} />
            </button>
          </article>
        ))}
      </div>
      <aside className="tip-strip">
        <span className="tip-icon"><Sparkles size={20} /></span>
        <p><strong>Petit conseil :</strong> commencez par les segments les plus courts. Ils offrent moins de possibilités.</p>
      </aside>
    </main>
  )
}

function LeaderboardPage({ nickname, day }) {
  const [data, setData] = useState({ entries: [], loading: true, error: '' })
  useEffect(() => {
    let active = true
    track('leaderboard_view', { mode: 'daily', source: 'app' })
    api.leaderboard(100, day)
      .then((result) => active && setData({ entries: result.entries || result.leaderboard || [], loading: false, error: '' }))
      .catch((error) => active && setData({ entries: [], loading: false, error: error.message }))
    return () => { active = false }
  }, [nickname, day])
  return (
    <main className="page section-shell leaderboard-page">
      <div className="page-heading page-heading--row">
        <div>
          <p className="eyebrow">Défi du {dayLabel(day)}</p>
          <h1>Classement du jour</h1>
          <p>Le chrono part au lancement de la grille. Une première réussite par joueur.</p>
        </div>
        <div className="leaderboard-emblem"><Trophy size={38} /></div>
      </div>
      <section className="leaderboard-panel">
        <div className="leaderboard-columns"><span>Rang</span><span>Joueur</span><span>Temps</span></div>
        {data.loading && <div className="loading-list" aria-label="Chargement"><i /><i /><i /></div>}
        {!data.loading && data.error && <p className="inline-error">{data.error}</p>}
        {!data.loading && !data.error && !data.entries.length && (
          <div className="leaderboard-empty-state">
            <BarChart3 size={34} />
            <h2>Le podium vous attend.</h2>
            <p>Les premiers résultats apparaîtront dès qu’une grille sera terminée.</p>
          </div>
        )}
        {!!data.entries.length && (
          <ol className="full-leaderboard">
            {data.entries.map((entry, index) => (
              <li key={entry.id || `${entry.nickname}-${index}`}>
                <span className={`rank rank--${index + 1}`}>{index + 1}</span>
                <span className="player-name">
                  <strong>{entry.nickname || entry.pseudo}</strong>
                  {(entry.assisted || entry.assisted === 1) && <small>assisté</small>}
                </span>
                <time>{formatTime(entry.elapsedSeconds ?? entry.elapsed_seconds ?? entry.time)}</time>
              </li>
            ))}
          </ol>
        )}
      </section>
      <p className="leaderboard-note">Les parties sans aide sont classées en premier, puis par temps de résolution.</p>
    </main>
  )
}

function RulesPage({ navigate }) {
  return (
    <main className="page section-shell rules-page">
      <div className="page-heading">
        <p className="eyebrow">Règles du Sukoji</p>
        <h1>Comptez. Croisez. Déduisez.</h1>
        <p>Le Sukoji ressemble à des mots croisés, mais les mots sont remplacés par des suites de nombres.</p>
      </div>
      <section className="rules-layout">
        <div className="rules-copy">
          <article className="rule-block">
            <span className="rule-index">1</span>
            <div><h2>Observez les segments</h2><p>Une ligne de cases blanches forme un segment. Il s’arrête au bord de la grille ou devant une case noire.</p></div>
          </article>
          <article className="rule-block">
            <span className="rule-index">2</span>
            <div><h2>Placez les nombres de 1 à n</h2><p>Dans un segment de 5 cases, placez une fois chacun des nombres 1, 2, 3, 4 et 5. Leur ordre est à découvrir.</p></div>
          </article>
          <article className="rule-block">
            <span className="rule-index">3</span>
            <div><h2>Croisez lignes et colonnes</h2><p>Chaque case respecte ses deux segments. Si une direction ne comporte qu’une case, seule l’autre direction compte.</p></div>
          </article>
          <article className="rule-callout">
            <Check size={22} />
            <p>La grille est terminée lorsque toutes les cases blanches sont remplies et chaque segment contient exactement les bons nombres, sans doublon.</p>
          </article>
          <div className="rules-actions">
            <button className="button button--primary" type="button" onClick={() => navigate('/jouer/quotidien')}>Essayer maintenant <ArrowRight size={18} /></button>
          </div>
        </div>
        <div className="rules-demo" aria-label="Exemple d’un segment de quatre cases">
          <p className="eyebrow">Exemple</p>
          <h2>Ce segment fait 4 cases</h2>
          <div className="segment-demo"><span>3</span><span>1</span><span>4</span><span>2</span></div>
          <p>Il contient bien les nombres <strong>1, 2, 3 et 4</strong>, une seule fois chacun.</p>
        </div>
      </section>
    </main>
  )
}

function ProfileModal({ open, nickname, onSave, onClose, required = false }) {
  const [value, setValue] = useState(nickname)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open) { setValue(nickname); setError('') } }, [open, nickname])
  const submit = async (event) => {
    event.preventDefault()
    const clean = value.normalize('NFC').trim().replace(/\s+/g, ' ')
    if (clean.length < 2 || clean.length > 20 || !/^[\p{L}\p{N} _-]+$/u.test(clean)) {
      setError('Choisissez 2 à 20 caractères : lettres, chiffres, espace, tiret ou _.')
      return
    }
    setSaving(true)
    try {
      await onSave(clean)
    } catch (saveError) {
      setError(saveError.message)
    } finally {
      setSaving(false)
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!required}
      eyebrow={required ? 'Bienvenue' : 'Votre profil'}
      title={required ? 'Comment doit-on vous appeler ?' : 'Modifier votre pseudo'}
      className="profile-modal"
    >
      <p className="modal-intro">Il sera visible dans le classement. Il reste enregistré sur cet appareil et vous pourrez le modifier à tout moment.</p>
      <form onSubmit={submit}>
        <label htmlFor="nickname">Votre pseudo</label>
        <div className={`input-wrap ${error ? 'has-error' : ''}`}>
          <input
            id="nickname"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="nickname"
            maxLength={20}
            placeholder="Ex. RenardMalin"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'nickname-error' : 'nickname-help'}
          />
          <span>{value.trim().length}/20</span>
        </div>
        {error && <p className="field-error" id="nickname-error" role="alert">{error}</p>}
        <button className="button button--primary button--full" type="submit" disabled={saving}>
          {saving ? 'Enregistrement…' : required ? 'Commencer à jouer' : 'Enregistrer'}
        </button>
        <p className="privacy-note" id="nickname-help">Aucun compte ni adresse e-mail nécessaire.</p>
      </form>
    </Modal>
  )
}

export default function App() {
  const { route, pathname, navigate } = useRouter()
  const [nickname, setNickname] = useState(readNickname)
  const [profileOpen, setProfileOpen] = useState(!nickname)
  const query = useMemo(() => new URLSearchParams(route.split('?')[1] || ''), [route])
  const requestedDifficulty = query.get('niveau') || 'easy'
  const trainingDifficulty = ['easy', 'medium', 'hard'].includes(requestedDifficulty)
    ? requestedDifficulty
    : 'easy'
  const requestedLeaderboardDay = query.get('jour')
  const leaderboardDay = isCalendarDay(requestedLeaderboardDay) ? requestedLeaderboardDay : null
  const requestedArchiveDay = query.get('jour')
  const archiveDay = isCalendarDay(requestedArchiveDay) ? requestedArchiveDay : null

  useEffect(() => {
    const synchronize = () => { syncPendingNickname() }
    synchronize()
    window.addEventListener('online', synchronize)
    return () => window.removeEventListener('online', synchronize)
  }, [])

  useEffect(() => {
    if (!ROUTES.has(pathname)) navigate('/', { replace: true })
    else if (pathname === '/jouer/entrainement' && requestedDifficulty !== trainingDifficulty) {
      navigate(`/jouer/entrainement?niveau=${trainingDifficulty}`, { replace: true })
    } else if (pathname === '/classement' && requestedLeaderboardDay && !leaderboardDay) {
      navigate('/classement', { replace: true })
    } else if (pathname === '/jouer/archive' && !archiveDay) {
      navigate('/archives', { replace: true })
    }
  }, [pathname, requestedDifficulty, trainingDifficulty, requestedLeaderboardDay, leaderboardDay, archiveDay, navigate])

  const saveNickname = (nextNickname) => {
    try {
      localStorage.setItem(NICKNAME_KEY, nextNickname)
      localStorage.setItem(PENDING_NICKNAME_KEY, nextNickname)
    } catch { /* navigation privée */ }
    setNickname(nextNickname)
    setProfileOpen(false)
    syncPendingNickname()
  }

  const storeDailyCompletion = useCallback(({ day, result, extra }) => {
    recordDailyCompletion(day, result, extra)
  }, [])

  const applyAccount = useCallback((account) => {
    if (!account?.nickname) return
    try { localStorage.setItem(NICKNAME_KEY, account.nickname) } catch { /* navigation privée */ }
    setNickname(account.nickname)
  }, [])

  let page
  if (pathname === '/entrainement') page = <TrainingPage navigate={navigate} />
  else if (pathname === '/classement') page = <LeaderboardPage nickname={nickname} day={leaderboardDay} />
  else if (pathname === '/regles') page = <RulesPage navigate={navigate} />
  else if (pathname === '/profil') page = <PlayerProgressPage nickname={nickname} onEditNickname={() => setProfileOpen(true)} navigate={navigate} onAccountChanged={applyAccount} />
  else if (pathname === '/archives') page = <ArchivesPage navigate={navigate} nickname={nickname} />
  else if (pathname === '/confidentialite') page = <PrivacyPage />
  else if (pathname === '/jouer/quotidien') page = <GamePage mode="daily" nickname={nickname} navigate={navigate} onDailyComplete={storeDailyCompletion} />
  else if (pathname === '/jouer/entrainement') {
    page = <GamePage mode="training" difficulty={trainingDifficulty} nickname={nickname} navigate={navigate} />
  } else if (pathname === '/jouer/archive' && archiveDay) {
    page = <GamePage mode="archive" difficulty="medium" archiveDay={archiveDay} nickname={nickname} navigate={navigate} />
  } else page = <HomePage navigate={navigate} nickname={nickname} />

  return (
    <div className="app-shell">
      <Header route={pathname} navigate={navigate} nickname={nickname} onProfile={() => navigate('/profil')} />
      {page}
      {!pathname.startsWith('/jouer/') && (
        <>
          <footer className="site-footer">
            <span>Sukoji</span>
            <p>Un petit défi logique, chaque jour.</p>
            <nav aria-label="Liens de pied de page">
              <button type="button" onClick={() => navigate('/regles')}>Règles</button>
              <button type="button" onClick={() => navigate('/archives')}>Archives</button>
              <button type="button" onClick={() => navigate('/confidentialite')}>Confidentialité</button>
            </nav>
          </footer>
          <BottomNavigation route={pathname} navigate={navigate} />
        </>
      )}
      <ProfileModal
        open={profileOpen}
        nickname={nickname}
        onSave={saveNickname}
        onClose={() => nickname && setProfileOpen(false)}
        required={!nickname}
      />
    </div>
  )
}
